import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  X,
  Plus,
  Calendar as CalendarIcon,
  Paperclip,
  Rocket,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Trash2,
  Building2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ideasApi, clientsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
function toISO(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function isoToDate(iso) {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function AttachmentIcon({ contentType, filename }) {
  const isImg = contentType?.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(filename || "");
  return isImg ? (
    <ImageIcon className="w-4 h-4 text-amber-500 shrink-0" />
  ) : (
    <FileText className="w-4 h-4 text-amber-500 shrink-0" />
  );
}

export function IdeaDialog({ open, onOpenChange, idea, styles, scopedClientId = null, onSaved, onStylesChanged, onLightbox }) {
  const isEdit = !!idea?.id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = !!user?.client_id;
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const isLaunched = !!idea?.launched_project_id;
  // Seed idea from the scoped client page carries a client_id but no `id`
  // — in that case treat this as a *create* (not edit) while pre-filling.

  const [title, setTitle] = useState(idea?.title || "");
  const [startDate, setStartDate] = useState(idea?.start_date || "");
  const [launchDate, setLaunchDate] = useState(idea?.launch_date || "");
  const [projectOwner, setProjectOwner] = useState(idea?.project_owner || "");
  const [clientId, setClientId] = useState(
    idea?.client_id || scopedClientId || user?.client_id || ""
  );
  const [clientDisplayName, setClientDisplayName] = useState(
    idea?.client_name || user?.client_name || ""
  );
  // "__other__" is the sentinel for the free-text path. On mount, if the
  // idea has a video_style_name but no id, we're already in Other mode.
  const initialStyleId = idea?.video_style_id
    ? idea.video_style_id
    : (idea?.video_style_name ? "__other__" : "");
  const [styleId, setStyleId] = useState(initialStyleId);
  const [otherStyleName, setOtherStyleName] = useState(
    idea?.video_style_id ? "" : (idea?.video_style_name || "")
  );
  const [notes, setNotes] = useState(idea?.notes || "");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [attachments, setAttachments] = useState(idea?.attachments || []);

  // Reset on open/close
  useEffect(() => {
    if (!open) return;
    setTitle(idea?.title || "");
    setStartDate(idea?.start_date || "");
    setLaunchDate(idea?.launch_date || "");
    setProjectOwner(idea?.project_owner || "");
    setClientId(idea?.client_id || scopedClientId || user?.client_id || "");
    setClientDisplayName(idea?.client_name || user?.client_name || "");
    const sid = idea?.video_style_id
      ? idea.video_style_id
      : (idea?.video_style_name ? "__other__" : "");
    setStyleId(sid);
    setOtherStyleName(idea?.video_style_id ? "" : (idea?.video_style_name || ""));
    setNotes(idea?.notes || "");
    setAttachments(idea?.attachments || []);
    setPendingFiles([]);
  }, [open, idea, user?.client_id, user?.client_name, scopedClientId]);

  // Look up the display name when we only have an id (scoped mode with no
  // client_name in the seed). Staff-only; client users always have their
  // own client_name on the user object.
  useEffect(() => {
    if (!open || isClient || clientDisplayName || !clientId) return;
    (async () => {
      try {
        const c = await clientsApi.get(clientId);
        setClientDisplayName(c?.name || "");
      } catch {
        /* no-op — leave display blank if lookup fails */
      }
    })();
  }, [open, isClient, clientId, clientDisplayName]);

  // Client selector was previously a dropdown for staff. It's now always
  // a locked display — ideas are created from within a specific client's
  // page, so the client is predetermined. If a future cross-client admin
  // view is added, re-introduce a picker there.
  // (Removed unused `clients` state / clientsApi.list effect.)

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    const good = [];
    for (const f of files) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`"${f.name}" exceeds 25MB`);
        continue;
      }
      good.push(f);
    }
    setPendingFiles((prev) => [...prev, ...good]);
    e.target.value = "";
  };

  const removePending = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveAttachment = async (attId) => {
    if (!isEdit) return;
    if (!window.confirm("Remove this file?")) return;
    try {
      await ideasApi.removeAttachment(idea.id, attId);
      setAttachments((prev) => prev.filter((a) => a.id !== attId));
      toast.success("Removed");
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const handleViewAttachment = async (att) => {
    if (!isEdit) return;
    try {
      const res = await ideasApi.attachmentSignedUrl(idea.id, att.id);
      const url = `${window.location.origin}${res.url}`;
      // Route image + PDF through the ChartViewer lightbox
      const type = att.content_type || "";
      const isImg = type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(att.filename || "");
      const isPdf = type === "application/pdf" || /\.pdf$/i.test(att.filename || "");
      if (isImg || isPdf) {
        onLightbox?.({ url, filename: att.filename, contentType: type });
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open");
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("start_date", startDate || "");
      fd.append("launch_date", launchDate || "");
      fd.append("project_owner", projectOwner.trim());
      if (!isClient) fd.append("client_id", clientId || "");
      // Style: either a concrete id, an "Other" free-text label, or blank.
      if (styleId && styleId !== "__other__") {
        fd.append("video_style_id", styleId);
      } else if (styleId === "__other__" && otherStyleName.trim()) {
        fd.append("video_style_name", otherStyleName.trim());
      } else {
        // Explicitly clear on edit if user picked "— Choose —"
        fd.append("video_style_id", "");
        fd.append("video_style_name", "");
      }
      fd.append("notes", notes.trim());
      for (const f of pendingFiles) fd.append("attachments", f);
      let saved;
      if (isEdit) {
        // PATCH is form-encoded; we can't add attachments in the same call.
        // Send text fields via PATCH, then upload each pending file individually.
        saved = await ideasApi.update(idea.id, fd);
        for (const f of pendingFiles) {
          const one = new FormData();
          one.append("file", f);
          await ideasApi.addAttachment(idea.id, one);
        }
      } else {
        saved = await ideasApi.create(fd);
      }
      toast.success(isEdit ? "Idea updated" : "Idea created");
      await onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLaunch = async () => {
    if (!isEdit || isLaunched) return;
    if (!window.confirm(`Launch "${idea.title}" as a real project? This will create a new production record.`))
      return;
    setLaunching(true);
    try {
      const res = await ideasApi.launch(idea.id);
      toast.success("Launched! Opening project…");
      onOpenChange(false);
      navigate(`/projects/${res.project_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.project_id) {
        toast("Already launched — opening the existing project", { icon: "🚀" });
        onOpenChange(false);
        navigate(`/projects/${detail.project_id}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Launch failed");
      }
    } finally {
      setLaunching(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !canDelete || isLaunched) return;
    if (!window.confirm(
      `Delete "${idea.title}"? This can't be undone. Attachments will be removed as well.`
    )) return;
    setDeleting(true);
    try {
      await ideasApi.remove(idea.id);
      toast.success("Idea deleted");
      await onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const activeStyles = useMemo(
    () => (styles || []).filter((s) => !s.is_archived),
    [styles]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="idea-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {isEdit ? "Edit idea" : "New idea"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Sketch a video project. Attach references. Launch when it's real.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Title */}
          <div>
            <Label htmlFor="idea-title" className="text-xs uppercase tracking-wider text-zinc-500">
              Title
            </Label>
            <Input
              id="idea-title"
              data-testid="idea-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1"
              placeholder="Q3 Product Reveal"
            />
          </div>

          {/* Date pickers */}
          <div className="grid grid-cols-2 gap-3">
            <DatePickerField
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              testId="idea-start-date"
            />
            <DatePickerField
              label="Launch Date"
              value={launchDate}
              onChange={setLaunchDate}
              testId="idea-launch-date"
            />
          </div>

          {/* Owner + Client */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Project Owner
              </Label>
              <Input
                data-testid="idea-owner-input"
                value={projectOwner}
                onChange={(e) => setProjectOwner(e.target.value)}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1"
                placeholder="e.g. Sarah Chen"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Client
              </Label>
              <div
                data-testid="idea-client-select"
                data-locked="true"
                className="mt-1 h-10 px-3 flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-md text-sm text-zinc-300"
              >
                <Building2 className="w-3.5 h-3.5 text-amber-500/70 shrink-0" />
                <span className="truncate">{clientDisplayName || "—"}</span>
              </div>
            </div>
          </div>

          {/* Video Style — master list + "Other — describe…" free-text */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Video Style
            </Label>
            <select
              data-testid="idea-style-select"
              value={styleId}
              onChange={(e) => {
                const v = e.target.value;
                setStyleId(v);
                if (v !== "__other__") setOtherStyleName("");
              }}
              className="mt-1 h-10 w-full px-3 bg-zinc-950 border border-zinc-800 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
            >
              <option value="">— Choose —</option>
              {activeStyles.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              <option value="__other__">Other — describe…</option>
            </select>
            {styleId === "__other__" && (
              <div className="mt-2">
                <Input
                  data-testid="idea-other-style-input"
                  value={otherStyleName}
                  onChange={(e) => setOtherStyleName(e.target.value)}
                  placeholder="Describe the style (an admin can promote it)"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                  maxLength={60}
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Not in the list? Type it here — an admin can promote your
                  label to the master list later.
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Notes / brief
            </Label>
            <Textarea
              data-testid="idea-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1"
              placeholder="Concept, audience, references, deliverables…"
            />
          </div>

          {/* Attachments */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Attachments
            </Label>
            <div className="mt-1 space-y-2">
              {attachments.map((a) => (
                <div
                  key={a.id}
                  data-testid={`idea-attachment-${a.id}`}
                  className="flex items-center gap-2 p-2 border border-zinc-800 rounded-md bg-zinc-900/40"
                >
                  <AttachmentIcon contentType={a.content_type} filename={a.filename} />
                  <button
                    type="button"
                    onClick={() => handleViewAttachment(a)}
                    className="flex-1 text-left text-sm text-zinc-200 hover:text-amber-400 truncate"
                    data-testid={`idea-attachment-view-${a.id}`}
                  >
                    {a.filename}
                  </button>
                  <span className="text-xs text-zinc-500 shrink-0">
                    {Math.round((a.size || 0) / 1024)} KB
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(a.id)}
                    data-testid={`idea-attachment-remove-${a.id}`}
                    className="p-1.5 text-zinc-500 hover:text-rose-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {pendingFiles.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2 border border-amber-500/40 rounded-md bg-amber-500/5"
                >
                  <Paperclip className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="flex-1 text-sm text-zinc-200 truncate">{f.name}</span>
                  <span className="text-xs text-zinc-500 shrink-0">
                    {Math.round(f.size / 1024)} KB · pending
                  </span>
                  <button
                    type="button"
                    onClick={() => removePending(i)}
                    className="p-1.5 text-zinc-500 hover:text-rose-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-zinc-800 rounded-md text-sm text-zinc-400 hover:border-amber-500/50 hover:text-amber-400 cursor-pointer">
                <Upload className="w-4 h-4" />
                Attach files (PDF, image, doc — up to 25MB each)
                <input
                  type="file"
                  multiple
                  onChange={handleFilePick}
                  data-testid="idea-attachment-input"
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Launched chip */}
          {isEdit && isLaunched && (
            <div
              data-testid="idea-launched-badge"
              className="p-3 border border-emerald-500/40 rounded-md bg-emerald-500/5 flex items-center gap-2 text-sm"
            >
              <Rocket className="w-4 h-4 text-emerald-400" />
              <span className="text-zinc-200 flex-1">
                Launched as a real project
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="idea-view-project-btn"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/projects/${idea.launched_project_id}`);
                }}
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              >
                View project <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6 gap-2 flex-col sm:flex-row">
          {isEdit && canDelete && !isLaunched && (
            <Button
              type="button"
              variant="outline"
              data-testid="idea-delete-btn"
              onClick={handleDelete}
              disabled={deleting}
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 sm:mr-auto"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </Button>
          )}
          {isEdit && !isLaunched && !isClient && (
            <Button
              type="button"
              data-testid="idea-launch-btn"
              onClick={handleLaunch}
              disabled={launching}
              className={`bg-emerald-500 text-black hover:bg-emerald-400 ${
                !canDelete ? "sm:mr-auto" : ""
              }`}
            >
              {launching ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Rocket className="w-4 h-4 mr-2" />
              )}
              Launch as project
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-800 text-zinc-300"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="idea-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 text-black hover:bg-amber-400"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DatePickerField({ label, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className="mt-1 h-10 w-full px-3 flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-left hover:border-zinc-700 focus:outline-none focus:ring-1 focus:ring-amber-500"
          >
            <CalendarIcon className="w-3.5 h-3.5 text-zinc-500" />
            <span className={value ? "text-zinc-100" : "text-zinc-500"}>
              {value ? fmtDate(value) : "Pick a date"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-zinc-950 border-zinc-800 p-0 w-auto" align="start">
          <Calendar
            mode="single"
            selected={isoToDate(value)}
            onSelect={(d) => {
              onChange(toISO(d));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
